/**
 * Legge il primo foglio di un .xlsx senza librerie: un xlsx e' uno zip di XML,
 * e il browser sa gia' decomprimere (DecompressionStream) e leggere XML
 * (DOMParser). Basta al modello delle vendite: testi, numeri, celle vuote.
 *
 * ponytail: niente date, formule ne' fogli oltre il primo (xl/worksheets/sheet1.xml,
 * senza risolvere workbook.xml); se servissero, e' il momento di una libreria.
 */
const NON_XLSX = 'Il file non e un Excel (.xlsx) leggibile'

async function fileDelloZip(dati: ArrayBuffer): Promise<Map<string, () => Promise<string>>> {
  const v = new DataView(dati)
  let fine = -1
  for (let i = dati.byteLength - 22; i >= Math.max(0, dati.byteLength - 65557); i--) {
    if (v.getUint32(i, true) === 0x06054b50) {
      fine = i
      break
    }
  }
  if (fine < 0) throw new Error(NON_XLSX)

  const voci = new Map<string, () => Promise<string>>()
  let p = v.getUint32(fine + 16, true)
  for (let n = v.getUint16(fine + 10, true); n > 0; n--) {
    if (v.getUint32(p, true) !== 0x02014b50) throw new Error(NON_XLSX)
    const metodo = v.getUint16(p + 10, true)
    const compresso = v.getUint32(p + 20, true)
    const lNome = v.getUint16(p + 28, true)
    const locale = v.getUint32(p + 42, true)
    const nome = new TextDecoder().decode(new Uint8Array(dati, p + 46, lNome))
    p += 46 + lNome + v.getUint16(p + 30, true) + v.getUint16(p + 32, true)

    voci.set(nome, async () => {
      const inizio = locale + 30 + v.getUint16(locale + 26, true) + v.getUint16(locale + 28, true)
      const byte = new Uint8Array(dati, inizio, compresso)
      if (metodo === 0) return new TextDecoder().decode(byte)
      const flusso = new Response(byte).body!.pipeThrough(new DecompressionStream('deflate-raw'))
      return new Response(flusso).text()
    })
  }
  return voci
}

/** «C12» -> 2 (colonna, da zero). */
function colonna(riferimento: string): number {
  let n = 0
  for (const c of riferimento.replace(/\d+$/, '')) n = n * 26 + (c.charCodeAt(0) - 64)
  return n - 1
}

const testo = (e: Element | null | undefined): string => e?.textContent ?? ''

export async function leggiFoglio(dati: ArrayBuffer): Promise<string[][]> {
  const voci = await fileDelloZip(dati)
  const foglio = voci.get('xl/worksheets/sheet1.xml')
  if (!foglio) throw new Error(NON_XLSX)

  const xml = (s: string) => new DOMParser().parseFromString(s, 'application/xml')
  const condivise = voci.get('xl/sharedStrings.xml')
  const stringhe = condivise
    ? Array.from(xml(await condivise()).getElementsByTagName('si'), (si) =>
        Array.from(si.getElementsByTagName('t'), (t) => t.textContent ?? '').join(''),
      )
    : []

  const righe: string[][] = []
  for (const r of Array.from(xml(await foglio()).getElementsByTagName('row'))) {
    const riga: string[] = []
    for (const c of Array.from(r.getElementsByTagName('c'))) {
      const tipo = c.getAttribute('t')
      const valore = testo(c.getElementsByTagName('v')[0])
      riga[colonna(c.getAttribute('r') ?? '')] =
        tipo === 's'
          ? (stringhe[Number(valore)] ?? '')
          : tipo === 'inlineStr'
            ? Array.from(c.getElementsByTagName('t'), (t) => t.textContent ?? '').join('')
            : valore
    }
    righe[Number(r.getAttribute('r')) - 1] = Array.from(riga, (x) => x ?? '')
  }
  return Array.from(righe, (x) => x ?? [])
}
